import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Loader2, Search, UserPlus, Users2 } from "lucide-react";
import { toast } from "react-toastify";

import { Input } from "../ui/input";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Quem está no grupo.
//
// A conversa mostra o nome de quem FALA; quem está calado era invisível. Para
// saber se o dono da empresa está no grupo antes de escrever alguma coisa ali
// dentro, a saída era abrir o WhatsApp no celular.
//
// A marca de "já está nos contatos" é a mesma da conversa, e o botão adiciona
// pela mesma rota (`PUT /contacts/:id/agenda`).
const ParticipantesDoGrupo = ({ ticketId }) => {
	const [participantes, setParticipantes] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [busca, setBusca] = useState("");
	const [adicionando, setAdicionando] = useState(null);

	const carregar = useCallback(
		(ativo = { current: true }) => {
			if (!ticketId) return Promise.resolve();

			setCarregando(true);

			return api
				.get(`/tickets/${ticketId}/participantes`)
				.then(({ data }) => {
					if (ativo.current) setParticipantes(Array.isArray(data) ? data : []);
				})
				.catch(() => undefined)
				.finally(() => {
					if (ativo.current) setCarregando(false);
				});
		},
		[ticketId],
	);

	useEffect(() => {
		const ativo = { current: true };

		// Em promessa: `carregar` liga o "carregando" e chamar direto no corpo do
		// efeito é render em cascata.
		Promise.resolve().then(() => carregar(ativo));

		return () => {
			ativo.current = false;
		};
	}, [carregar]);

	const adicionar = async (participante) => {
		setAdicionando(participante.jid);

		try {
			if (participante.contactId) {
				await api.put(`/contacts/${participante.contactId}/agenda`);
			} else {
				// Participante que nunca falou não tem linha em Contacts: cria com
				// o número como nome, que é o que o WhatsApp mostra também.
				await api.post("/contacts", {
					name: participante.nome,
					number: participante.numero,
				});
			}

			toast.success(`${participante.nome} agora está nos seus contatos.`);
			await carregar();
		} catch (err) {
			toastError(err);
		}

		setAdicionando(null);
	};

	const filtrados = participantes.filter(
		(p) =>
			!busca.trim() ||
			p.nome.toLowerCase().includes(busca.trim().toLowerCase()) ||
			p.numero.includes(busca.replace(/\D/g, "")),
	);

	return (
		<div className="rounded-xl border bg-card p-4">
			<p className="mb-2 flex items-center gap-2 text-sm font-semibold">
				<Users2 className="h-4 w-4 text-muted-foreground" />
				Participantes
				{participantes.length > 0 && (
					<span className="text-xs font-normal text-muted-foreground">
						({participantes.length})
					</span>
				)}
				{carregando && (
					<Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
				)}
			</p>

			{!carregando && participantes.length === 0 ? (
				<p className="text-xs text-muted-foreground">
					Não foi possível ler os participantes — a conexão precisa estar
					ligada e o grupo precisa ser do WhatsApp.
				</p>
			) : (
				<>
					{participantes.length > 8 && (
						<div className="relative mb-2">
							<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={busca}
								onChange={(e) => setBusca(e.target.value)}
								placeholder="Procurar participante"
								className="h-8 pl-8 text-xs"
							/>
						</div>
					)}

					<ul className="max-h-72 divide-y overflow-y-auto rounded-lg border">
						{filtrados.map((p) => (
							<li
								key={p.jid}
								className="flex items-center gap-2 px-2.5 py-1.5 text-sm"
							>
								<span className="min-w-0 flex-1">
									<span className="flex items-center gap-1">
										<span className="truncate">{p.nome}</span>
										{p.admin && (
											<span className="shrink-0 rounded-full bg-muted px-1.5 text-[10px] uppercase text-muted-foreground">
												admin
											</span>
										)}
									</span>
									<span className="block truncate text-xs text-muted-foreground">
										{p.numero}
									</span>
								</span>

								{p.naAgenda ? (
									<BadgeCheck
										className="h-4 w-4 shrink-0 text-emerald-500"
										title="Já está nos seus contatos"
									/>
								) : (
									<button
										type="button"
										onClick={() => adicionar(p)}
										disabled={adicionando === p.jid}
										title="Adicionar aos contatos"
										aria-label={`Adicionar ${p.nome} aos contatos`}
										className="flex shrink-0 items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-50"
									>
										{adicionando === p.jid ? (
											<Loader2 className="h-3 w-3 animate-spin" />
										) : (
											<UserPlus className="h-3 w-3" />
										)}
										adicionar
									</button>
								)}
							</li>
						))}
					</ul>
				</>
			)}
		</div>
	);
};

export default ParticipantesDoGrupo;
